import type { LineItem } from '../components/tickets/LineItemCard';
import type {
  RepairshoprTicketLineItem,
  LineItemOverride,
} from '@techgitter/contracts';
import type {
  TicketTypeTemplate,
  TemplateLineItem,
  AdditionalDetailsValues,
  OverrideState,
  TemplateCustomerContext,
  TicketDraft,
  PricingRuleResult,
  PricingRuleHook,
  AdditionalField,
  FieldCondition,
  AdditionalFieldOption,
  FieldDefaultResolver,
} from '../types/ticketTypeTemplate';
import { getProductBySku } from '../hooks/useProductCatalog';
import { hydratedUnitCost } from './nonChargeablePrice';

// Phase 2 will populate this registry from the blob-storage template fetch.
// Rules are keyed by the name string stored in template JSON.
export const PRICING_RULE_REGISTRY: Record<string, PricingRuleHook> = {};

// Resolvers that compute an additionalField's default value on template
// selection. Keyed by the name string stored in template JSON.
export const FIELD_DEFAULT_RESOLVER_REGISTRY: Record<string, FieldDefaultResolver> = {};

export function createDefaultOverrideState(): OverrideState {
  return {
    lineDetail: false,
    quantity: false,
    unitCost: false,
    prefixUserBase: false,
    afterHours: false,
    nonChargeable: false,
  };
}

const SHORT_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/**
 * How a value reads inside a template's text. A date field stores ISO
 * `yyyy-MM-dd`, which is what the ticket keeps (and what pricing rules and
 * follow-ups read), but a subject or line detail is for a person, so it
 * renders `dd MMM` — `2026-11-09` → `09 Nov`. Anything else is unchanged.
 */
export function formatTemplateValue(value: string | number): string {
  if (typeof value === 'string') {
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
    const month = m ? Number(m[2]) : 0;
    if (m && month >= 1 && month <= 12) return `${m[3]} ${SHORT_MONTHS[month - 1]}`;
  }
  return String(value);
}

export function interpolateTemplate(
  template: string,
  variables: AdditionalDetailsValues
): string {
  let result = template;

  // Evaluate conditional blocks: {?key}...{/key} — kept if variable is truthy, removed otherwise
  const conditionalRegex = /\{\?(\w+)\}([\s\S]*?)\{\/\1\}/g;
  result = result.replace(conditionalRegex, (_match, key: string, content: string) => {
    const value = variables[key];
    if (value !== undefined && value !== null && value !== '') {
      return content;
    }
    return '';
  });

  // Replace {variable} placeholders with values
  Object.entries(variables).forEach(([key, value]) => {
    const regex = new RegExp(`\\{${key}\\}`, 'g');
    result = result.replace(regex, formatTemplateValue(value));
  });

  // Remove any unresolved variables
  const unresolvedVariableRegex = /\{[^}/?][^}]*\}/g;
  result = result.replace(unresolvedVariableRegex, '');

  result = result.replace(/\n{3,}/g, '\n\n');
  result = result.replace(/  +/g, ' ');
  result = result.trim();

  return result;
}

function applyPricingRule(
  templateLineItem: TemplateLineItem,
  lineItemDraft: LineItem,
  variables: AdditionalDetailsValues,
  customerContext: TemplateCustomerContext | undefined,
  ticketDraft: TicketDraft,
  now?: Date
): PricingRuleResult {
  if (!templateLineItem.pricingRule) {
    return {};
  }

  const rule = PRICING_RULE_REGISTRY[templateLineItem.pricingRule];
  if (!rule) {
    return {};
  }

  try {
    const result = rule({
      customer: customerContext || {},
      now: now || new Date(),
      additionalDetails: variables,
      lineItemDraft,
      ticketDraft,
      base: {
        unitCost: lineItemDraft.unitCost,
        quantity: lineItemDraft.quantity,
        lineDetail: lineItemDraft.lineDetail,
        prefixUserBase: lineItemDraft.prefixUserBase,
      },
    });

    return result || {};
  } catch (error) {
    console.error('Pricing rule error:', error);
    return {};
  }
}

export function generateLineItemFromTemplate(
  templateLineItem: TemplateLineItem,
  variables: AdditionalDetailsValues,
  customerContext?: TemplateCustomerContext,
  ticketDraft?: TicketDraft,
  now?: Date
): LineItem {
  // Resolve the line's own catalog product once — its `description` column is
  // exposed to the detail/prefix templates as {productDescription}, and its
  // unitCost is the pricing base (both keyed on this line's fixed lineItemId SKU).
  const catalogProduct = getProductBySku(templateLineItem.lineItemId);
  const detailVariables = { ...variables, productDescription: catalogProduct?.description ?? '' };

  const lineDetail = templateLineItem.lineDetailTemplate
    ? interpolateTemplate(templateLineItem.lineDetailTemplate, detailVariables)
    : '';

  const prefixUserBase = templateLineItem.prefixTemplate
    ? interpolateTemplate(templateLineItem.prefixTemplate, detailVariables)
    : templateLineItem.category;

  const baseLineItem: LineItem = {
    id: crypto.randomUUID(),
    source: 'ticketTypeTemplate',
    templateKey: templateLineItem.templateKey,
    lineItemId: templateLineItem.lineItemId,
    category: templateLineItem.category,
    quantity: templateLineItem.defaultQuantity,
    unitCost: catalogProduct?.unitCost ?? templateLineItem.defaultUnitCost ?? 0,
    prefixUserBase,
    afterHours: templateLineItem.afterHours || false,
    nonChargeable: templateLineItem.nonChargeable || false,
    lineDetail,
    subtotal: 0,
    finalTotal: 0,
    overrideState: createDefaultOverrideState(),
    pricingExplanation: undefined,
  };

  const pricingResult = applyPricingRule(
    templateLineItem,
    baseLineItem,
    variables,
    customerContext,
    ticketDraft || { lineItems: [baseLineItem] },
    now
  );

  if (pricingResult.lineItemId) {
    baseLineItem.lineItemId = pricingResult.lineItemId;
  }

  if (pricingResult.unitCost !== undefined) {
    baseLineItem.unitCost = pricingResult.unitCost;
  }

  if (pricingResult.quantity !== undefined) {
    baseLineItem.quantity = pricingResult.quantity;
  }

  if (pricingResult.lineDetailAppend) {
    baseLineItem.lineDetail = baseLineItem.lineDetail
      ? `${baseLineItem.lineDetail}\n${pricingResult.lineDetailAppend}`
      : pricingResult.lineDetailAppend;
  }

  if (pricingResult.explanation) {
    baseLineItem.pricingExplanation = pricingResult.explanation;
  }

  const subtotal = baseLineItem.quantity * baseLineItem.unitCost;
  baseLineItem.subtotal = subtotal;
  baseLineItem.finalTotal = baseLineItem.nonChargeable ? 0 : subtotal;

  return baseLineItem;
}

export function generateLineItemsFromTemplate(
  template: TicketTypeTemplate,
  variables: AdditionalDetailsValues,
  customerContext?: TemplateCustomerContext,
  ticketDraft?: TicketDraft,
  now?: Date
): LineItem[] {
  if (!template.templateLineItems) {
    return [];
  }

  const draftContext = ticketDraft || { lineItems: [], ticketTypeId: template.ticketTypeId };

  return template.templateLineItems.map(templateLineItem =>
    generateLineItemFromTemplate(templateLineItem, variables, customerContext, draftContext, now)
  );
}

export function updateLineItemFromTemplate(
  existingLineItem: LineItem,
  templateLineItem: TemplateLineItem,
  variables: AdditionalDetailsValues,
  customerContext?: TemplateCustomerContext,
  ticketDraft?: TicketDraft,
  now?: Date
): LineItem {
  const updates: Partial<LineItem> = {};
  const overrideState = existingLineItem.overrideState || createDefaultOverrideState();
  let hasChanges = false;

  if (!overrideState.prefixUserBase && templateLineItem.prefixTemplate) {
    const newPrefixUserBase = interpolateTemplate(templateLineItem.prefixTemplate, variables);
    if (newPrefixUserBase !== existingLineItem.prefixUserBase) {
      updates.prefixUserBase = newPrefixUserBase;
      hasChanges = true;
    }
  }

  const draftContext = ticketDraft || {
    lineItems: [existingLineItem],
    ticketTypeId: templateLineItem.templateKey
  };

  const pricingResult = applyPricingRule(
    templateLineItem,
    existingLineItem,
    variables,
    customerContext,
    draftContext,
    now
  );

  // Compute lineDetail atomically (template + optional pricing append) so that
  // a transient render where the pricing rule briefly emits no append can't
  // leave the field on a template-only value with the blurb stripped.
  if (!overrideState.lineDetail) {
    let candidate: string | undefined;
    if (templateLineItem.lineDetailTemplate) {
      const productDescription = getProductBySku(templateLineItem.lineItemId)?.description ?? '';
      candidate = interpolateTemplate(templateLineItem.lineDetailTemplate, { ...variables, productDescription });
    }
    if (pricingResult.lineDetailAppend) {
      candidate = candidate
        ? `${candidate}\n${pricingResult.lineDetailAppend}`
        : pricingResult.lineDetailAppend;
    }
    if (candidate !== undefined && candidate !== existingLineItem.lineDetail) {
      updates.lineDetail = candidate;
      hasChanges = true;
    }
  }

  if (pricingResult.lineItemId && pricingResult.lineItemId !== existingLineItem.lineItemId) {
    updates.lineItemId = pricingResult.lineItemId;
    hasChanges = true;
  }

  if (!overrideState.quantity) {
    const newQuantity = pricingResult.quantity !== undefined
      ? pricingResult.quantity
      : templateLineItem.defaultQuantity;
    if (newQuantity !== existingLineItem.quantity) {
      updates.quantity = newQuantity;
      hasChanges = true;
    }
  }

  if (!overrideState.unitCost) {
    const effectiveLineItemId = pricingResult.lineItemId ?? templateLineItem.lineItemId;
    const catalogUnitCost = getProductBySku(effectiveLineItemId)?.unitCost ?? templateLineItem.defaultUnitCost ?? 0;
    const newUnitCost = pricingResult.unitCost !== undefined
      ? pricingResult.unitCost
      : catalogUnitCost;
    if (newUnitCost !== existingLineItem.unitCost) {
      updates.unitCost = newUnitCost;
      hasChanges = true;
    }
  }

  if (!overrideState.afterHours && templateLineItem.afterHours !== undefined) {
    if (templateLineItem.afterHours !== existingLineItem.afterHours) {
      updates.afterHours = templateLineItem.afterHours;
      hasChanges = true;
    }
  }

  if (!overrideState.nonChargeable && templateLineItem.nonChargeable !== undefined) {
    if (templateLineItem.nonChargeable !== existingLineItem.nonChargeable) {
      updates.nonChargeable = templateLineItem.nonChargeable;
      hasChanges = true;
    }
  }

  const newPricingExplanation = pricingResult.explanation || undefined;
  if (newPricingExplanation !== existingLineItem.pricingExplanation) {
    updates.pricingExplanation = newPricingExplanation;
    hasChanges = true;
  }

  if (!hasChanges) {
    return existingLineItem;
  }

  const updatedItem = { ...existingLineItem, ...updates };

  const subtotal = updatedItem.quantity * updatedItem.unitCost;
  const finalTotal = updatedItem.nonChargeable ? 0 : subtotal;

  if (subtotal === existingLineItem.subtotal && finalTotal === existingLineItem.finalTotal) {
    return {
      ...updatedItem,
    };
  }

  return {
    ...updatedItem,
    subtotal,
    finalTotal,
  };
}

export function syncTemplateLineItems(
  currentLineItems: LineItem[],
  template: TicketTypeTemplate | null,
  variables: AdditionalDetailsValues,
  suppressedTemplateKeys: Set<string> = new Set(),
  customerContext?: TemplateCustomerContext,
  ticketDraft?: TicketDraft,
  now?: Date
): LineItem[] {
  // `rs` items are lines that already exist on the RepairShopr ticket. They
  // are never regenerated from the template and never dropped here: in edit
  // mode this function runs as soon as hydration resolves a template, so
  // dropping them would clear the form of the ticket's real billable lines —
  // and once edit-mode save diffs the form against RS, that clearing would be
  // pushed to RepairShopr as a deletion.
  const rsItems = currentLineItems.filter(item => item.source === 'rs');
  const manualItems = currentLineItems.filter(item => item.source === 'manual');

  if (!template || !template.templateLineItems) {
    const kept = [...rsItems, ...manualItems];
    if (kept.length === currentLineItems.length) {
      return currentLineItems;
    }
    return kept;
  }

  const templateItems = currentLineItems.filter(item => item.source === 'ticketTypeTemplate');

  const updatedTemplateItems: LineItem[] = [];
  let hasChanges = false;

  // Adoption (below) may stamp a template key onto a saved line, so work
  // against a copy rather than the filtered original.
  let resolvedRsItems = rsItems;
  let rsChanged = false;
  const adoptedRsIds = new Set<string>();

  // A SKU held by more than one saved line can't be adopted unambiguously.
  // Skipping is the safe failure: it generates a fresh line (a visible
  // duplicate the user can delete) rather than silently binding a template
  // key to the wrong charge.
  const rsSkuCounts = new Map<string, number>();
  for (const item of rsItems) {
    rsSkuCounts.set(item.lineItemId, (rsSkuCounts.get(item.lineItemId) ?? 0) + 1);
  }

  const draftContext = ticketDraft || {
    lineItems: currentLineItems,
    ticketTypeId: template.ticketTypeId,
  };

  template.templateLineItems.forEach(templateLineItem => {
    if (suppressedTemplateKeys.has(templateLineItem.templateKey)) {
      return;
    }

    const existingItem = templateItems.find(
      item => item.templateKey === templateLineItem.templateKey
    );

    if (existingItem) {
      const updatedItem = updateLineItemFromTemplate(existingItem, templateLineItem, variables, customerContext, draftContext, now);
      updatedTemplateItems.push(updatedItem);
      if (updatedItem !== existingItem) {
        hasChanges = true;
      }
      return;
    }

    // Already saved to RepairShopr under this template key — leave it be.
    // Regenerating would mint a fresh id with no `rsLineItemId`, which the
    // edit-mode diff reads as a new line and POSTs, duplicating it. The saved
    // line is also the priced one: `convertRsLineItemsToFormLineItems` marks
    // every field user-owned, so the template would not overwrite its values
    // even if we did run it through `updateLineItemFromTemplate`.
    const savedItem = resolvedRsItems.find(
      item => item.templateKey === templateLineItem.templateKey
    );
    if (savedItem) {
      return;
    }

    const generated = generateLineItemFromTemplate(
      templateLineItem, variables, customerContext, draftContext, now
    );

    // Tickets saved before template keys were persisted have saved lines with
    // no key at all. Claim one by SKU so their first edit-save updates the
    // existing charge instead of duplicating it. Matched against the
    // *generated* SKU, not `templateLineItem.lineItemId`, because a pricing
    // rule may have rewritten it (see `pricingResult.lineItemId` above) —
    // which is exactly the license lines where a duplicate costs money.
    const adoptableIndex = resolvedRsItems.findIndex(item =>
      !item.templateKey &&
      !adoptedRsIds.has(item.id) &&
      item.lineItemId === generated.lineItemId
    );
    if (adoptableIndex !== -1 && rsSkuCounts.get(generated.lineItemId) === 1) {
      const claimed = resolvedRsItems[adoptableIndex];
      resolvedRsItems = [...resolvedRsItems];
      resolvedRsItems[adoptableIndex] = {
        ...claimed,
        templateKey: templateLineItem.templateKey,
      };
      adoptedRsIds.add(claimed.id);
      rsChanged = true;
      return;
    }

    updatedTemplateItems.push(generated);
    hasChanges = true;
  });

  if (
    !hasChanges &&
    !rsChanged &&
    updatedTemplateItems.length === templateItems.length &&
    rsItems.length + manualItems.length + templateItems.length === currentLineItems.length
  ) {
    return currentLineItems;
  }

  // Saved lines first, then whatever this template is adding, then manual —
  // so a new template line appears under the ticket's existing charges.
  return [...resolvedRsItems, ...updatedTemplateItems, ...manualItems];
}

export function markFieldAsOverridden(
  lineItem: LineItem,
  fieldName: keyof OverrideState
): LineItem {
  const overrideState = lineItem.overrideState || createDefaultOverrideState();
  
  return {
    ...lineItem,
    overrideState: {
      ...overrideState,
      [fieldName]: true,
    },
  };
}

export function resetFieldOverride(
  lineItem: LineItem,
  fieldName: keyof OverrideState
): LineItem {
  const overrideState = lineItem.overrideState || createDefaultOverrideState();
  
  return {
    ...lineItem,
    overrideState: {
      ...overrideState,
      [fieldName]: false,
    },
  };
}

export function isTemplateLineItem(lineItem: LineItem): boolean {
  return lineItem.source === 'ticketTypeTemplate' && !!lineItem.templateKey;
}

export function getTemplateLineItemsByTemplate(
  lineItems: LineItem[],
  template: TicketTypeTemplate
): LineItem[] {
  const templateKeys = new Set(
    template.templateLineItems?.map(t => t.templateKey) || []
  );

  return lineItems.filter(
    item => item.source === 'ticketTypeTemplate' &&
    item.templateKey &&
    templateKeys.has(item.templateKey)
  );
}

export function removeTemplateLineItems(
  lineItems: LineItem[],
  template: TicketTypeTemplate | null
): LineItem[] {
  if (!template) {
    return lineItems;
  }

  const templateKeys = new Set(
    template.templateLineItems?.map(t => t.templateKey) || []
  );

  return lineItems.filter(
    item => !(item.source === 'ticketTypeTemplate' && item.templateKey && templateKeys.has(item.templateKey))
  );
}

export function evaluateFieldCondition(
  condition: FieldCondition,
  additionalDetails: AdditionalDetailsValues,
  customer: TemplateCustomerContext
): boolean {
  if (condition.type === 'custom' && condition.customEvaluator) {
    try {
      return condition.customEvaluator({ additionalDetails, customer });
    } catch (error) {
      console.error('Custom field condition evaluator error:', error);
      return false;
    }
  }

  if (condition.type === 'fieldValue' && condition.fieldKey) {
    const fieldValue = additionalDetails[condition.fieldKey];

    switch (condition.operator) {
      case 'equals':
        return fieldValue === condition.value;
      case 'notEquals':
        return fieldValue !== condition.value;
      case 'greaterThan':
        return typeof fieldValue === 'number' && fieldValue > (condition.value as number);
      case 'lessThan':
        return typeof fieldValue === 'number' && fieldValue < (condition.value as number);
      case 'contains':
        return typeof fieldValue === 'string' && fieldValue.includes(String(condition.value));
      case 'in':
        return Array.isArray(condition.value) && condition.value.includes(fieldValue as never);
      default:
        return false;
    }
  }

  if (condition.type === 'customerContext') {
    const contextValue = customer[condition.fieldKey || ''];

    switch (condition.operator) {
      case 'equals':
        return contextValue === condition.value;
      case 'notEquals':
        return contextValue !== condition.value;
      case 'contains':
        return typeof contextValue === 'string' && contextValue.includes(String(condition.value));
      case 'in':
        return Array.isArray(condition.value) && condition.value.includes(contextValue as never);
      default:
        return false;
    }
  }

  return false;
}

export function shouldShowField(
  field: AdditionalField,
  additionalDetails: AdditionalDetailsValues,
  customer: TemplateCustomerContext
): boolean {
  if (!field.showWhen) {
    return true;
  }

  return evaluateFieldCondition(field.showWhen, additionalDetails, customer);
}

export function applyDropdownPriority(
  field: AdditionalField,
  additionalDetails: AdditionalDetailsValues,
  customer: TemplateCustomerContext
): AdditionalFieldOption[] {
  if (!field.options) {
    return [];
  }

  if (!field.optionPriorityResolver) {
    return field.options;
  }

  try {
    return field.optionPriorityResolver({
      customer,
      additionalDetails,
      options: field.options,
    });
  } catch (error) {
    console.error('Dropdown priority resolver error:', error);
    return field.options;
  }
}

/**
 * Convert RS line items + DV overrides into form LineItem[] for edit mode.
 * All override flags are set to true so the form treats existing values as user-owned,
 * preserving them across template changes.
 */
export function convertRsLineItemsToFormLineItems(
  rsItems: RepairshoprTicketLineItem[],
  overrides: LineItemOverride[],
): LineItem[] {
  const overrideMap = new Map(overrides.map(o => [o.rsLineItemId, o]));
  const allTrue: OverrideState = {
    lineDetail: true,
    quantity: true,
    unitCost: true,
    prefixUserBase: true,
    afterHours: true,
    nonChargeable: true,
  };

  return rsItems
    .filter(item => !item.converted)
    .map(item => {
      const override = overrideMap.get(item.id);
      const quantity = parseFloat(item.quantity) || 0;
      const unitCost = hydratedUnitCost(item.price_retail, override);
      const nonChargeable = override?.nonChargeable ?? false;
      const subtotal = quantity * unitCost;

      return {
        id: String(item.id),
        source: 'rs' as const,
        rsLineItemId: item.id,
        converted: false,
        lineItemId: item.name,
        // Which template line minted this charge, when one did. Lets
        // `syncTemplateLineItems` recognise it as already saved instead of
        // regenerating it, and the edit-mode diff PATCH it instead of POSTing
        // a duplicate. Undefined on manual lines and pre-templateKey tickets.
        templateKey: override?.templateKey,
        category: override?.category ?? '',
        quantity,
        unitCost,
        prefixUserBase: override?.prefixUserBase ?? '',
        afterHours: override?.afterHours ?? false,
        nonChargeable,
        lineDetail: item.description,
        subtotal,
        finalTotal: nonChargeable ? 0 : subtotal,
        overrideState: allTrue,
      };
    });
}
